import { GAME_CONFIG } from '../config/game-config'
import { Feedback } from '../audio/feedback'
import { balanceTargetForLevel } from '../domain/rules'
import type { Point } from '../domain/types'
import { PointerInput } from '../input/pointer-input'
import { GameWorld } from '../physics/game-world'
import { GameRenderer } from '../rendering/game-renderer'
import { GameSession } from '../state/game-session'

const requiredElement = <T extends HTMLElement>(selector: string): T => {
  const element = document.querySelector<T>(selector)
  if (!element) throw new Error(`Missing required element: ${selector}`)
  return element
}

const createSeed = (): number => {
  const values = new Uint32Array(1)
  crypto.getRandomValues(values)
  return values[0] ?? Date.now()
}

export class KrawoomApp {
  private readonly frame = requiredElement<HTMLElement>('#game-frame')
  private readonly canvas = requiredElement<HTMLCanvasElement>('#game-canvas')
  private readonly levelElement = requiredElement<HTMLElement>('#level')
  private readonly balanceValueElement = requiredElement<HTMLElement>('#balance-value')
  private readonly balanceTrack = requiredElement<HTMLElement>('#balance-track')
  private readonly balanceIndicator = requiredElement<HTMLElement>('#balance-indicator')
  private readonly balanceTargetMarker = requiredElement<HTMLElement>('#balance-target-marker')
  private readonly balanceBestMarker = requiredElement<HTMLElement>('#balance-best-marker')
  private readonly balanceReadout = requiredElement<HTMLElement>('#balance-value-wrap')
  private readonly balanceLimitReadout = requiredElement<HTMLElement>('#balance-limit')
  private readonly balanceLimitValue = requiredElement<HTMLElement>('#balance-limit-value')
  private readonly boomsElement = requiredElement<HTMLElement>('#booms')
  private readonly hintElement = requiredElement<HTMLElement>('#hint')
  private readonly statusElement = requiredElement<HTMLElement>('#status-label')
  private readonly pauseOverlay = requiredElement<HTMLElement>('#pause-overlay')
  private readonly overlayKicker = requiredElement<HTMLElement>('#overlay-kicker')
  private readonly overlayTitle = requiredElement<HTMLElement>('#overlay-title')
  private readonly overlayDetail = requiredElement<HTMLElement>('#overlay-detail')
  private readonly nextLevelButton = requiredElement<HTMLButtonElement>('#next-level-button')
  private readonly pauseButton = requiredElement<HTMLButtonElement>('#pause-button')
  private readonly resetButton = requiredElement<HTMLButtonElement>('#reset-button')
  private readonly soundButton = requiredElement<HTMLButtonElement>('#sound-button')
  private readonly controlsElement = requiredElement<HTMLElement>('.command-deck .controls')
  private readonly session = new GameSession()
  private readonly feedback = new Feedback()
  private readonly renderer = new GameRenderer(this.canvas)
  private readonly input: PointerInput
  private world!: GameWorld
  private animationFrame = 0
  private previousTime = performance.now()
  private accumulatorMs = 0
  private resizeTimer = 0
  private started = false

  constructor() {
    this.rebuildWorld()
    this.input = new PointerInput(
      this.canvas,
      () => ({ width: this.world.width, height: this.world.height }),
      (point) => this.fire(point),
    )
    this.bindControls()
    this.bindLifecycle()
    this.animationFrame = requestAnimationFrame((time) => this.loop(time))
  }

  destroy(): void {
    cancelAnimationFrame(this.animationFrame)
    clearTimeout(this.resizeTimer)
    this.input.destroy()
    this.world.destroy()
  }

  private fire(point: Point): void {
    if (this.session.paused) return
    void this.feedback.unlock()
    const result = this.world.explode(point)
    this.session.registerBoom()
    this.renderer.addExplosion(result.visualCenter, result.protectedId !== null)
    this.feedback.explosion(result.protectedId !== null)
    this.started = true
    this.hintElement.classList.add('dismissed')
    this.updateHud()
  }

  private loop(now: number): void {
    const rawDelta = now - this.previousTime
    this.previousTime = now
    const frameDelta = Math.min(Math.max(0, rawDelta), GAME_CONFIG.maxFrameDeltaMs)

    if (!this.session.paused) {
      this.accumulatorMs += frameDelta
      let steps = 0
      while (
        this.accumulatorMs >= GAME_CONFIG.fixedStepMs &&
        steps < GAME_CONFIG.maxCatchUpSteps
      ) {
        this.world.step(GAME_CONFIG.fixedStepMs)
        this.accumulatorMs -= GAME_CONFIG.fixedStepMs
        steps += 1
      }
      if (steps === GAME_CONFIG.maxCatchUpSteps) this.accumulatorMs = 0
      this.handleWorldEvents()
      this.checkLevelGoal()
    } else {
      this.accumulatorMs = 0
    }

    // Visual effects must finish while paused too, especially after Game Over.
    this.renderer.update(frameDelta / 1000)
    this.renderer.render(this.world.snapshot())
    this.updateHud()
    this.animationFrame = requestAnimationFrame((time) => this.loop(time))
  }

  private handleWorldEvents(): void {
    for (const event of this.world.drainEvents()) {
      this.renderer.handleWorldEvent(event)
      if (event.type === 'damage') this.feedback.damage(event.amount)
      else this.feedback.death()
    }
  }

  private rebuildWorld(): void {
    const bounds = this.frame.getBoundingClientRect()
    const width = Math.max(320, Math.round(bounds.width))
    const height = Math.max(300, Math.round(bounds.height))
    this.world?.destroy()
    this.world = new GameWorld(width, height, createSeed())
    this.renderer.resize(width, height)
    this.session.restartLevel()
    this.previousTime = performance.now()
    this.accumulatorMs = 0
    this.started = false
    this.hintElement?.classList.remove('dismissed')
    this.syncPauseUi()
    this.updateHud()
  }

  private updateHud(): void {
    const balance = this.world.relativeBalance
    const target = balanceTargetForLevel(this.session.level)
    const indicatorPosition = (balance + 100) / 2
    const targetPosition = (target + 100) / 2
    const bestBalance = this.world.bestPossibleBalance
    const bestPosition = (bestBalance + 100) / 2
    let actualReadoutPosition = indicatorPosition
    let limitReadoutPosition = bestPosition
    if (Math.abs(indicatorPosition - bestPosition) < 14) {
      actualReadoutPosition = Math.min(95, indicatorPosition + 8)
      limitReadoutPosition = Math.max(5, bestPosition - 8)
    }
    const absoluteBalance = Math.abs(balance)

    this.levelElement.textContent = String(this.session.level).padStart(2, '0')
    this.balanceValueElement.textContent = `${balance >= 0 ? '+' : '-'}${absoluteBalance.toFixed(0)}`
    this.balanceTrack.dataset.direction = balance > 0.5 ? 'square' : balance < -0.5 ? 'circle' : 'even'
    this.balanceTrack.setAttribute('aria-valuenow', balance.toFixed(1))
    this.balanceTrack.setAttribute(
      'aria-valuetext',
      `${balance >= 0 ? 'Square' : 'Circle'} advantage ${absoluteBalance.toFixed(0)} percentage points; target plus ${target} percentage points`,
    )
    this.balanceIndicator.style.left = `${indicatorPosition}%`
    this.balanceTargetMarker.style.left = `${targetPosition}%`
    this.balanceBestMarker.style.left = `${bestPosition}%`
    this.balanceReadout.style.left = `${actualReadoutPosition}%`
    this.balanceLimitReadout.style.left = `${limitReadoutPosition}%`
    this.balanceLimitValue.textContent = `+${bestBalance.toFixed(0)}`
    this.balanceBestMarker.setAttribute('aria-hidden', 'true')
    this.boomsElement.textContent = String(this.session.booms)
  }

  private bindControls(): void {
    this.pauseButton.addEventListener('click', () => this.togglePause())
    this.resetButton.addEventListener('click', () => this.rebuildWorld())
    this.nextLevelButton.addEventListener('click', () => {
      this.session.advanceLevel()
      this.rebuildWorld()
    })
    this.soundButton.addEventListener('click', () => {
      const muted = this.feedback.toggleMute()
      this.soundButton.textContent = muted ? '×' : '♪'
      this.soundButton.setAttribute('aria-pressed', String(muted))
      this.soundButton.setAttribute('aria-label', muted ? 'Enable sound' : 'Mute sound')
    })
    window.addEventListener('keydown', (event) => {
      if (event.repeat) return
      if (event.key === 'p' || event.key === 'P' || event.key === 'Escape') this.togglePause()
      if (event.key === 'r' || event.key === 'R') this.rebuildWorld()
      if (event.key === 'm' || event.key === 'M') this.soundButton.click()
    })
  }

  private bindLifecycle(): void {
    document.addEventListener('visibilitychange', () => {
      this.session.setLifecyclePaused(document.hidden)
      this.previousTime = performance.now()
      this.syncPauseUi()
    })
    window.addEventListener('blur', () => {
      this.session.setLifecyclePaused(true)
      this.syncPauseUi()
    })
    window.addEventListener('focus', () => {
      if (!document.hidden) this.session.setLifecyclePaused(false)
      this.previousTime = performance.now()
      this.syncPauseUi()
    })
    window.addEventListener('resize', () => {
      clearTimeout(this.resizeTimer)
      this.resizeTimer = window.setTimeout(() => this.rebuildWorld(), 180)
    })
  }

  private togglePause(): void {
    if (this.session.levelCleared || this.session.gameOver) return
    this.session.toggleManualPause()
    this.previousTime = performance.now()
    this.syncPauseUi()
  }

  private syncPauseUi(): void {
    const paused = this.session.paused
    this.pauseOverlay.hidden = !paused
    const levelCleared = this.session.levelCleared
    const gameOver = this.session.gameOver
    this.overlayKicker.textContent = levelCleared
      ? 'BALANCE TARGET REACHED'
      : gameOver
        ? 'TARGET UNREACHABLE'
        : 'SIMULATION HELD'
    this.overlayTitle.textContent = levelCleared
      ? `LEVEL ${this.session.level} CLEAR`
      : gameOver
        ? 'GAME OVER'
        : 'PAUSED'
    this.overlayDetail.textContent = levelCleared
      ? 'Squares control the balance'
      : gameOver
        ? `Best possible: +${this.world.bestPossibleBalance.toFixed(0)} relative balance`
        : 'Tap resume or press P'
    this.nextLevelButton.hidden = !levelCleared
    this.controlsElement.hidden = gameOver
    this.pauseButton.textContent = paused && !levelCleared && !gameOver ? '▶' : 'Ⅱ'
    this.pauseButton.disabled = levelCleared || gameOver
    this.pauseButton.setAttribute(
      'aria-label',
      levelCleared
        ? 'Level complete'
        : gameOver
          ? 'Game over'
          : paused
            ? 'Resume game'
            : 'Pause game',
    )
    this.pauseButton.setAttribute('aria-pressed', String(paused && !levelCleared && !gameOver))
    this.statusElement.textContent = levelCleared
      ? 'CLEARED'
      : gameOver
        ? 'GAME OVER'
        : paused
          ? 'HELD'
          : 'LIVE'
    if (!paused && !this.started) this.hintElement.classList.remove('dismissed')
  }

  private checkLevelGoal(): void {
    if (
      !this.session.levelCleared &&
      this.world.relativeBalance >= balanceTargetForLevel(this.session.level)
    ) {
      this.session.completeLevel()
      this.feedback.levelComplete()
      this.syncPauseUi()
      return
    }
    if (
      !this.session.gameOver &&
      !this.session.levelCleared &&
      this.world.bestPossibleBalance < balanceTargetForLevel(this.session.level)
    ) {
      this.session.endGame()
      this.syncPauseUi()
    }
  }
}
